"use client";

// Next.js imports

// Sanity.io imports
import { PortableText } from "next-sanity";

// React imports
import { useState } from "react";

// Hooks, Utility & Helper function imports
import { createPortableTextComponents } from "@/utils/portableTextComponents";
import imagePlaceholder from "@/utils/imagePlaceholder";
import NextImage from "./NextImage";
import { t } from "@/utils/t";

interface GalleryImageProps {
  url: any;
  originalFilename: any;
  size: any;
  clickToEnlarge: any;
  alignment: any;
  rounded: any;
  caption: any;
  locale?: string;
}

const SIZES: any = {
  small: 640,
  medium: 1152,
  large: 2560,
};

const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL ?? "/";

export default function GalleryImage({
  url,
  originalFilename,
  size = "large",
  clickToEnlarge,
  alignment,
  rounded,
  caption,
  locale = "en",
}: GalleryImageProps) {
  const ptComponents = createPortableTextComponents(locale);
  const [enlargedImage, setEnlargedImage] = useState(false);

  const placeholder = `${BASE_URL}images/placeholder-image.png`;

  return (
    <>
      <div className="relative w-full">
        <NextImage
          src={url || placeholder}
          alt={originalFilename || "Image"}
          placeholder="blur"
          blurDataURL={imagePlaceholder(SIZES[size], SIZES[size])}
          onClick={() =>
            clickToEnlarge &&
            setEnlargedImage((currentState: boolean) => !currentState)
          }
          className={`w-full image-${size || "large"} image-${alignment || "center"} ${clickToEnlarge ? "cursor-pointer hover:scale-105 hover:shadow-lg transition-all" : ""} ${rounded ? "rounded-medium overflow-hidden" : "rounded-none"}`}
          maxWidth={SIZES[size]}
          width={SIZES[size]}
          height={SIZES[size]}
          isOptimized
          priority
        />
        {caption && (
          <div className="caption p-4 text-center bg-glacier rounded-b-medium">
            <PortableText
              value={t(caption, locale)}
              components={ptComponents}
              onMissingComponent={(message, options) => {
                console.warn(message, {
                  type: options.type,
                  nodeType: options.nodeType,
                });
              }}
            />
          </div>
        )}
      </div>

      {clickToEnlarge && (
        <div
          className={`${enlargedImage ? "block" : "hidden"} fixed top-0 left-0 w-screen h-screen cursor-pointer overflow-hidden z-50`}
          onClick={(e) =>
            setEnlargedImage((currentState: boolean) => !currentState)
          }
        >
          <div className="absolute w-full h-full bg-black pointer-events-none opacity-90"></div>
          <div className="absolute w-full h-full p-8 pointer-events-none">
            <div className="w-full h-full">
              <NextImage
                src={url || placeholder}
                alt={originalFilename || "Image"}
                placeholder="blur"
                blurDataURL={imagePlaceholder(2560, 2560)}
                onClick={(e: any) =>
                  setEnlargedImage((currentState: boolean) => !currentState)
                }
                className={`h-full w-full object-contain image-large image-center ${rounded ? "rounded-medium overflow-hidden" : "rounded-none"}`}
                maxWidth={2560}
                width={2560}
                height={2560}
                isOptimized
                priority
              />
            </div>
            <div className="absolute top-0 right-0 w-10 h-10 m-8 text-white headline-1">
              ×
            </div>
          </div>
        </div>
      )}
    </>
  );
}
